/**
 * SF-122: Shared tick-based duration formatter — days, optionally + real hours/minutes.
 *
 * Ticks are Utopian days per project convention (1 tick = 1 day, docs/TIME.md). The backend
 * computes `ticksRemaining = ceil(returnTime - currentTick)` and
 * `returnTimeFraction = frac(returnTime)` (`ProvinceDtoMapper.java`) — since `currentTick` is
 * always integral, the true continuous remaining duration is reconstructed here rather than
 * requiring every caller to redo the arithmetic.
 *
 * Supersedes the deleted `formatTicksRemaining()` (`utils/gameTime.ts`), which was dead code with
 * a formula that double-counted a tick whenever there was a fractional remainder.
 */

export interface FormatDurationOptions {
  /** Fractional part of the backend's continuous `returnTime`, in `[0, 1)`. */
  returnTimeFraction?: number;
  /** Real milliseconds per tick, used to convert the sub-day remainder to hours/minutes. */
  tickIntervalMs?: number;
  /** When true, also render whole minutes (full-precision army-return countdowns). */
  includeMinutes?: boolean;
}

/** Absorbs floating-point noise from the `frac()` reconstruction near integer-day boundaries. */
const EPSILON = 1e-9;

const MS_PER_HOUR = 3_600_000;
const MS_PER_MINUTE = 60_000;

export function formatDuration(ticksRemaining: number, opts?: FormatDurationOptions): string {
  // Malformed-input guard: an out-of-range/non-finite returnTimeFraction is ignored entirely
  // (treated as 0) rather than propagating NaN.
  const rawFraction = opts?.returnTimeFraction;
  const returnTimeFraction =
    typeof rawFraction === 'number' && Number.isFinite(rawFraction) && rawFraction >= 0 && rawFraction < 1
      ? rawFraction
      : 0;

  // Same treatment for a non-finite/non-positive tickIntervalMs — hours/minutes conversion is
  // skipped (falls back to the days-only path) rather than dividing by an invalid interval.
  const rawInterval = opts?.tickIntervalMs;
  const tickIntervalMs =
    typeof rawInterval === 'number' && Number.isFinite(rawInterval) && rawInterval > 0
      ? rawInterval
      : undefined;

  // Reconstructed exact remaining ticks — currentTick is always integral, so the fractional
  // component only ever applies once, against the ceil()'d ticksRemaining.
  const exactRemaining = returnTimeFraction > 0
    ? ticksRemaining - 1 + returnTimeFraction
    : ticksRemaining;

  // Clamp target is the reconstructed value, not the raw parameter.
  if (!Number.isFinite(exactRemaining) || exactRemaining <= EPSILON) {
    return '0d';
  }

  const days = Math.floor(exactRemaining + EPSILON);
  const subDayRemainder = exactRemaining - days;

  // This is the only path that produces the whole-day form — an exact-one-day input always
  // renders "1d", never "1d 0h".
  if (subDayRemainder <= EPSILON || tickIntervalMs === undefined) {
    return `${days}d`;
  }

  const remainderMs = subDayRemainder * tickIntervalMs;
  const hours = Math.floor(remainderMs / MS_PER_HOUR);

  if (!opts?.includeMinutes) {
    // days === 0: suppress the leading "0d " unit, matching the mockup's "5h left" display.
    return days === 0 ? `${hours}h` : `${days}d ${hours}h`;
  }

  const minutesRemainderMs = remainderMs - hours * MS_PER_HOUR;
  const minutes = Math.floor(minutesRemainderMs / MS_PER_MINUTE);

  return days === 0 ? `${hours}h ${minutes}m` : `${days}d ${hours}h ${minutes}m`;
}
